/**
 * Bookmark Panel Component
 * Displays all bookmarked messages with folder organization
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useBookmarkFolders } from '@/hooks/useBookmarkFolders';
import type { Bookmark } from '@/types/api.types';

interface BookmarkPanelProps {
  onClose: () => void;
  onNavigateToMessage?: (messageId: string, channelId?: string, dmThreadId?: string) => void;
}

export const BookmarkPanel = ({ onClose, onNavigateToMessage }: BookmarkPanelProps) => {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const { bookmarks, loading, removeBookmark } = useBookmarks(selectedFolder);
  const { folders } = useBookmarkFolders();

  // Count unfiled bookmarks
  const unfiledCount = bookmarks.filter(b => !b.folder_id).length;

  const handleRemoveBookmark = async (messageId: string) => {
    try {
      await removeBookmark(messageId);
      toast.success('Запазването е премахнато');
    } catch {
      toast.error('Грешка при премахване');
    }
  };

  const handleNavigate = (bookmark: Bookmark) => {
    if (onNavigateToMessage) {
      const channelId = bookmark.messages.channel_id || undefined;
      const dmThreadId = bookmark.messages.dm_thread_id || undefined;
      onNavigateToMessage(bookmark.message_id, channelId, dmThreadId);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bg-BG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-bg-primary">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border-primary">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
          Запазени съобщения
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-500 dark:text-dark-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Folder Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Folders List */}
        <div className="w-48 border-r border-gray-200 dark:border-dark-border-primary overflow-y-auto">
          {/* All Bookmarks */}
          <button
            onClick={() => setSelectedFolder(null)}
            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors ${
              selectedFolder === null ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-dark-text-secondary'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>Всички</span>
              <span className="text-xs bg-gray-200 dark:bg-dark-bg-tertiary px-2 py-0.5 rounded-full">
                {bookmarks.length}
              </span>
            </div>
          </button>

          {/* Unfiled */}
          <button
            onClick={() => setSelectedFolder('unfiled')}
            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors ${
              selectedFolder === 'unfiled' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-dark-text-secondary'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>Без папка</span>
              <span className="text-xs bg-gray-200 dark:bg-dark-bg-tertiary px-2 py-0.5 rounded-full">
                {unfiledCount}
              </span>
            </div>
          </button>

          <div className="h-px bg-gray-200 dark:bg-dark-border-primary my-2" />

          {/* Folders */}
          {folders.map(folder => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolder(folder.id)}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors ${
                selectedFolder === folder.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-dark-text-secondary'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {folder.icon && <span>{folder.icon}</span>}
                  <span className="truncate">{folder.name}</span>
                </div>
                <span className="text-xs bg-gray-200 dark:bg-dark-bg-tertiary px-2 py-0.5 rounded-full ml-2">
                  {folder._count?.message_bookmarks || 0}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Bookmarks List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <svg className="w-16 h-16 text-gray-400 dark:text-dark-text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                {selectedFolder ? 'Няма запазени съобщения в тази папка' : 'Нямате запазени съобщения'}
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-2">
                Кликнете на звездичката при съобщение, за да го запазите
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-dark-border-primary">
              {bookmarks.map(bookmark => (
                <div
                  key={bookmark.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors cursor-pointer"
                  onClick={() => handleNavigate(bookmark)}
                >
                  {/* Message Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* User Avatar */}
                      {bookmark.messages.users.avatar_url ? (
                        <img
                          src={bookmark.messages.users.avatar_url}
                          alt={bookmark.messages.users.display_name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                          {bookmark.messages.users.display_name.charAt(0).toUpperCase()}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900 dark:text-dark-text-primary">
                            {bookmark.messages.users.display_name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                            {formatDate(bookmark.messages.created_at)}
                          </span>
                        </div>
                        {bookmark.messages.channels && (
                          <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                            #{bookmark.messages.channels.name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveBookmark(bookmark.message_id);
                      }}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                      title="Премахни запазването"
                    >
                      <svg className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Message Content */}
                  <div className="text-sm text-gray-700 dark:text-dark-text-secondary line-clamp-3 mb-2">
                    {bookmark.messages.content}
                  </div>

                  {/* Note */}
                  {bookmark.note && (
                    <div className="text-xs text-gray-500 dark:text-dark-text-muted italic border-l-2 border-gray-300 dark:border-dark-border-secondary pl-2 mt-2">
                      {bookmark.note}
                    </div>
                  )}

                  {/* Attachments Indicator */}
                  {bookmark.messages.message_attachments && bookmark.messages.message_attachments.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span>{bookmark.messages.message_attachments.length} прикачени файла</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
